export interface ActiveMatchSummary {
  id: string;
  gameKind: "arena" | "puzzle";
  mode: "1v1" | "2v2";
  phase: string;
  playerCount: number;
  startedAt?: string;
}

export async function fetchActiveMatches(signal?: AbortSignal): Promise<ActiveMatchSummary[]> {
  const request: RequestInit = {
    credentials: "same-origin",
    headers: { Accept: "application/json" }
  };
  if (signal) request.signal = signal;
  const endpoint = new URL("/api/admin/matches/active", location.origin);
  if (isLocalDevelopmentHost(location.hostname)) {
    endpoint.searchParams.set("devAdmin", "1");
  }
  const response = await fetch(`${endpoint.pathname}${endpoint.search}`, request);
  if (!response.ok) {
    throw new Error(`진행 중 경기 조회 실패 (${response.status})`);
  }
  const body = await response.json() as unknown;
  const matches = Array.isArray(body) ? body : readMatches(body);
  return matches.map(normalizeMatch).filter((match): match is ActiveMatchSummary => match !== null);
}

function isLocalDevelopmentHost(hostname: string): boolean {
  return hostname === "127.0.0.1" ||
    hostname === "localhost" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    /^172\.(?:1[6-9]|2\d|3[01])\./u.test(hostname);
}

function readMatches(body: unknown): unknown[] {
  if (body !== null && typeof body === "object" && "matches" in body) {
    const matches = (body as { matches?: unknown }).matches;
    return Array.isArray(matches) ? matches : [];
  }
  return [];
}

function normalizeMatch(value: unknown): ActiveMatchSummary | null {
  if (value === null || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.id !== "string") return null;
  const match: ActiveMatchSummary = {
    id: raw.id,
    gameKind: raw.gameKind === "puzzle" ? "puzzle" : "arena",
    mode: raw.mode === "2v2" || raw.mode === "team" ? "2v2" : "1v1",
    phase: typeof raw.phase === "string" ? raw.phase : "playing",
    playerCount: typeof raw.playerCount === "number" ? raw.playerCount
      : (typeof raw.humans === "number" ? raw.humans : 0) + (typeof raw.bots === "number" ? raw.bots : 0)
  };
  if (typeof raw.startedAt === "string") match.startedAt = raw.startedAt;
  return match;
}
